/**
 * Phase 27b — Weekly breakdown row.
 *
 * Renders `Regular Xh · Overtime Yh · Doubletime Zh` for the timesheet
 * ONLY when OT or DT is non-zero (defect 2 — plain weeks show nothing
 * so the plain totals card carries the message on its own).
 *
 * Tooltip on OT / DT labels surfaces a policy-aware, human-readable
 * summary — distinct copy per policy (defect 1):
 *   - california → "California overtime — 1.5× over 8 hrs/day or 40
 *                   hrs/week, 2× over 12 hrs/day; 7th-day rule
 *                   applies." (with the 7th-day fragment gated by
 *                   `seventh_day_rule_enabled`)
 *   - flsa       → "Federal FLSA — 1.5× for hours worked over 40 in a
 *                   workweek. No daily overtime threshold."
 *   - custom     → composed from the actual tenant thresholds.
 *   - unknown / missing → generic "Tenant overtime policy" fallback so
 *                   the tooltip still fires (rather than silently
 *                   disappearing on tenants with sparse config).
 *
 * Source of truth is `paySummary.policy` embedded on the timesheet
 * response — no extra network fetch (this component is used inside
 * pages that already load the timesheet).
 *
 * Props:
 *   paySummary — timesheet.pay_summary object
 *   testid     — optional data-testid suffix, default "weekly-breakdown"
 */
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";
import { Info } from "lucide-react";

const POLICY_LABELS = {
  california: "California",
  flsa: "FLSA (federal)",
  custom: "Custom",
};

function fmtH(m) {
  const n = Number(m || 0) / 60;
  return `${n.toFixed(n % 1 === 0 ? 0 : 2)}h`;
}

/**
 * Format a distinctive, human-readable one-liner per policy type.
 * Falls back to a composed threshold sentence when the shape is
 * unfamiliar so we never render an empty tooltip.
 */
function policyText(policy) {
  if (!policy) return "Workspace overtime policy";
  const name = policy.name;

  if (name === "california") {
    const dailyOt = policy.daily_ot_threshold_hours ?? 8;
    const dailyDt = policy.daily_dt_threshold_hours ?? 12;
    const weeklyOt = policy.weekly_ot_threshold_hours ?? 40;
    const seventh = policy.seventh_day_rule_enabled
      ? "; 7th consecutive day rule applies"
      : "";
    return `California overtime — 1.5× over ${dailyOt} hrs/day or ${weeklyOt} hrs/week, 2× over ${dailyDt} hrs/day${seventh}.`;
  }

  if (name === "flsa") {
    const weeklyOt = policy.weekly_ot_threshold_hours ?? 40;
    return `Federal FLSA — 1.5× for hours worked over ${weeklyOt} in a workweek. No daily overtime threshold.`;
  }

  if (name === "custom") {
    const bits = [];
    if (policy.daily_ot_threshold_hours) {
      bits.push(`daily OT after ${policy.daily_ot_threshold_hours}h`);
    }
    if (policy.daily_dt_threshold_hours) {
      bits.push(`doubletime after ${policy.daily_dt_threshold_hours}h`);
    }
    if (policy.weekly_ot_threshold_hours) {
      bits.push(`weekly OT after ${policy.weekly_ot_threshold_hours}h`);
    }
    if (policy.seventh_day_rule_enabled) {
      bits.push("7th-day rule on");
    }
    const body = bits.length ? ` — ${bits.join(", ")}` : "";
    return `Custom workspace policy${body}.`;
  }

  // Unknown policy name — best-effort recap from whatever thresholds
  // are present so the tooltip still carries meaning.
  const label = POLICY_LABELS[name] || name || "Workspace";
  const bits = [];
  if (policy.daily_ot_threshold_hours) {
    bits.push(`daily OT after ${policy.daily_ot_threshold_hours}h`);
  }
  if (policy.daily_dt_threshold_hours) {
    bits.push(`doubletime after ${policy.daily_dt_threshold_hours}h`);
  }
  if (policy.weekly_ot_threshold_hours) {
    bits.push(`weekly OT after ${policy.weekly_ot_threshold_hours}h`);
  }
  if (policy.seventh_day_rule_enabled) {
    bits.push("7th-day rule on");
  }
  return bits.length
    ? `${label} policy — ${bits.join(", ")}.`
    : `${label} overtime policy`;
}

function BreakdownStat({ label, minutes, tone, tooltip, testid }) {
  const toneCls =
    tone === "amber"
      ? "text-amber-600"
      : tone === "destructive"
        ? "text-destructive"
        : "text-primary";
  const body = (
    <div
      className="flex items-center gap-2"
      data-testid={testid}
    >
      <span className="flex items-center gap-1 text-xs uppercase tracking-widest text-muted-foreground">
        {label}
        {tooltip && <Info className="h-3 w-3" aria-hidden="true" />}
      </span>
      <span className={`font-display text-base font-semibold tabular-nums ${toneCls}`}>
        {fmtH(minutes)}
      </span>
    </div>
  );
  if (!tooltip) return body;
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className="cursor-help focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
            aria-label={`${label} — ${tooltip}`}
          >
            {body}
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs" data-testid={`${testid}-tooltip`}>
          <div className="text-xs leading-relaxed">{tooltip}</div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export default function WeeklyBreakdown({
  paySummary, testid = "weekly-breakdown",
}) {
  if (!paySummary?.totals) return null;
  const totals = paySummary.totals;
  // Coerce null/undefined to 0 explicitly — a strict === 0 check would
  // miss null and let the row render on plain weeks (defect 2).
  const otMinutes = Number(totals.ot_minutes) || 0;
  const dtMinutes = Number(totals.dt_minutes) || 0;
  const regMinutes = Number(totals.reg_minutes) || 0;

  // Only render when there's OT or DT — otherwise the plain totals
  // card above is already sufficient and this row would be noise.
  if (otMinutes === 0 && dtMinutes === 0) return null;

  const tooltip = policyText(paySummary.policy);

  return (
    <div
      className="flex flex-wrap items-center gap-4 rounded-lg border bg-accent/30 px-4 py-3 text-sm"
      data-testid={testid}
    >
      <BreakdownStat
        label="Regular"
        minutes={regMinutes}
        tone="primary"
        testid={`${testid}-reg`}
      />
      <span className="text-muted-foreground" aria-hidden="true">·</span>
      <BreakdownStat
        label="Overtime"
        minutes={otMinutes}
        tone="amber"
        tooltip={tooltip}
        testid={`${testid}-ot`}
      />
      {dtMinutes > 0 && (
        <>
          <span className="text-muted-foreground" aria-hidden="true">·</span>
          <BreakdownStat
            label="Doubletime"
            minutes={dtMinutes}
            tone="destructive"
            tooltip={tooltip}
            testid={`${testid}-dt`}
          />
        </>
      )}
    </div>
  );
}
